/**
 * SessionTimeoutGuard — layered, SOC2/ISO27001-aligned session policy for the
 * authenticated surfaces (/admin and /tools).
 *
 *   • Idle timeout (server-enforced; this is the UX layer): logs the user out
 *     after no activity. Sliding — any click/keypress/scroll resets the clock.
 *   • Warning modal ~60s before idle logout ("Stay signed in?").
 *   • Absolute session cap: hard re-auth after the cap, regardless of activity.
 *
 * The server (admin_auth.py) is the source of truth and enforces the same
 * limits; this component drives the precise user-facing logout + warning.
 *
 * Policy values are fetched from GET /api/auth/session so the numbers live in
 * one place (the backend).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ShieldAlert } from "lucide-react";
import api from "../lib/api";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "wheel"];

export default function SessionTimeoutGuard({ redirectTo = "/admin/login" }) {
  const [policy, setPolicy] = useState(null); // { idleMs, warnMs, absoluteDeadline }
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const lastActivity = useRef(Date.now());
  const warningRef = useRef(false);
  const loggingOut = useRef(false);

  // Load the canonical policy from the backend. Retries on transient failure
  // so a momentary hiccup doesn't leave the guard dormant for the session.
  useEffect(() => {
    let alive = true;
    let retry;
    const loadPolicy = async () => {
      try {
        const { data } = await api.get("/auth/session");
        if (!alive) return;
        const startedAt = data.session_started_at ? new Date(data.session_started_at).getTime() : Date.now();
        setPolicy({
          idleMs: (data.idle_timeout_seconds || 1800) * 1000,
          warnMs: (data.warning_seconds || 60) * 1000,
          absoluteDeadline: startedAt + (data.absolute_cap_seconds || 43200) * 1000,
        });
      } catch (e) {
        // 401 = not authenticated → stay dormant. Other errors → retry in 60s.
        if (alive && e?.response?.status !== 401) {
          retry = setTimeout(loadPolicy, 60000);
        }
      }
    };
    loadPolicy();
    return () => { alive = false; if (retry) clearTimeout(retry); };
  }, []);

  const doLogout = useCallback(async (reason) => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    try { await api.post(`/auth/logout?reason=${encodeURIComponent(reason)}`); } catch { /* ignore */ }
    const sep = redirectTo.includes("?") ? "&" : "?";
    window.location.assign(`${redirectTo}${sep}reason=${reason}`);
  }, [redirectTo]);

  const staySignedIn = useCallback(async () => {
    lastActivity.current = Date.now();
    warningRef.current = false;
    setWarning(false);
    try { await api.post("/auth/session/heartbeat"); } catch { /* ignore */ }
  }, []);

  // Activity listeners — ignored while the warning modal is open so the
  // countdown only clears on an explicit "Stay signed in".
  useEffect(() => {
    if (!policy) return undefined;
    let last = 0;
    const onActivity = () => {
      if (warningRef.current) return;
      const now = Date.now();
      if (now - last < 1000) return; // throttle
      last = now;
      lastActivity.current = now;
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
  }, [policy]);

  // Single ticking loop drives the warning + both logout conditions.
  useEffect(() => {
    if (!policy) return undefined;
    const tick = setInterval(() => {
      const now = Date.now();
      if (now >= policy.absoluteDeadline) { doLogout("session_cap"); return; }
      const idleFor = now - lastActivity.current;
      if (idleFor >= policy.idleMs) { doLogout("idle"); return; }
      if (idleFor >= policy.idleMs - policy.warnMs) {
        warningRef.current = true;
        setWarning(true);
        setCountdown(Math.max(0, Math.ceil((policy.idleMs - idleFor) / 1000)));
      } else if (warningRef.current) {
        warningRef.current = false;
        setWarning(false);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [policy, doLogout]);

  if (!warning) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="session-timeout-modal">
      <div className="bg-white max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center gap-2 text-amber-700 mb-3">
          <ShieldAlert size={18} />
          <span className="text-[12px] font-semibold uppercase tracking-[0.16em]">Session about to expire</span>
        </div>
        <h3 className="ek-heading text-lg text-black mb-2">Are you still there?</h3>
        <p className="text-[13.5px] text-zinc-600 leading-relaxed mb-5">
          For security, you&rsquo;ll be signed out in{" "}
          <strong data-testid="session-timeout-countdown">{countdown}s</strong> due to inactivity.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={staySignedIn}
            className="ek-btn ek-btn-primary text-[13px] flex-1"
            data-testid="session-stay-signed-in"
          >
            Stay signed in
          </button>
          <button
            type="button"
            onClick={() => doLogout("idle")}
            className="ek-btn ek-btn-outline text-[13px]"
            data-testid="session-sign-out-now"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
